import { useRef, useState } from 'react';
import { AnimatePresence } from 'motion/react';
import type {
  IframePoolItem,
  IframePoolOfferOdds,
  JustPulledItem,
  PoolOfferDetail,
  PoolOfferListItem,
  TierBadge,
  VolatilityLevel,
} from '../../../modules/api';
import { HelpLink } from '../../../modules/help';
import type { CategoryPillItem } from '../../../modules/design-system';
import { DemoRipButton } from '../../../modules/demo';
import { FairnessTicketKeeper } from '../../../modules/fairness/ui';
import { BottomAction, MOBILE_RAIL_SPACER_HEIGHT } from './BottomAction';
import { isOfferAvailable, statusLabel } from './offerStatus';
import { OddsPanel, OddsPanelSkeleton } from './OddsPanel';
import { PackInfoCard } from './PackInfoCard';
import { Slider } from './Slider';
import { usePhoneSliderRoom } from './usePhoneSliderRoom';
import { WhatsInsideButton, WhatsInsideSheet } from '../WhatsInside';
import { PotentialHitsByTier } from '../PotentialHits';
import { JustPulledStrip } from '../JustPulled';

/**
 * Grid layout for the glass pack-detail page (Figma 19074:253777).
 *
 *   < 800px  → single column, the buy dock fixed at the bottom
 *   ≥ 800px  → two columns (hero | 360px sidebar of glass cards)
 *   ≥ 1200px → the Figma desktop: 1170 of content, a 440 sidebar 93 away
 */

export interface PackLayoutProps {
  /** Currently-centered offer's detail — drives info card, odds, RIP price. */
  offer: PoolOfferDetail | null;
  /** Every pack in the active category — drives the slider slots. */
  categoryOffers: PoolOfferListItem[];
  /** Active offer id (slider centre). */
  activeOfferId: number | null;
  odds: IframePoolOfferOdds | null;
  maxForPurchaseCount: number | undefined;
  potentialHits: IframePoolItem[];
  potentialHitsLoading: boolean;
  justPulledItems: JustPulledItem[];
  justPulledLoading: boolean;
  onShowJustPulled?: () => void;
  categories: CategoryPillItem[];
  activeCategoryId?: string;
  /** Header pill click — updates URL + active gameId. */
  onCategorySelect: (id: string) => void;
  /** Slider swipe — updates active offer in-page, keeps URL stable. */
  onSlideChange?: (offerId: number) => void;
  onTierSelect?: (poolOfferId: number) => void;
  quantity?: number;
  onQuantityChange?: (next: number) => void;
  onBuy?: () => void;
  unitPrice?: number | string;
  volatility: VolatilityLevel;
  onVolatilityChange: (level: VolatilityLevel) => void;
  /** Free demo rip — `pack.demoMode` AND an embed_key in hand. */
  showDemo?: boolean;
  onOpenDemo?: () => void;
}

export function PackLayout({
  offer,
  categoryOffers,
  activeOfferId,
  odds,
  maxForPurchaseCount,
  potentialHits,
  potentialHitsLoading,
  justPulledItems,
  justPulledLoading,
  onShowJustPulled,
  categories: _categories,
  activeCategoryId: _activeCategoryId,
  onCategorySelect: _onCategorySelect,
  onSlideChange,
  quantity = 1,
  onQuantityChange,
  onBuy,
  unitPrice = 0,
  volatility,
  onVolatilityChange,
  showDemo = false,
  onOpenDemo,
}: PackLayoutProps) {
  // Not purchasable (e.g. restocking): the action bar stays, locked, and
  // reads the status instead of the price.
  const unavailableLabel =
    offer && !isOfferAvailable(offer.status) ? statusLabel(offer.status) : undefined;
  // Phone: potential hits and recent pulls live in the "What's inside?" sheet.
  const [whatsInsideOpen, setWhatsInsideOpen] = useState(false);
  // Phone: the slider takes whatever height the rest of the page leaves, so
  // the storefront fits one screen.
  const layoutRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const phoneRoom = usePhoneSliderRoom(layoutRef, sliderRef);

  // Convert category-offers list to TierBadge[] so PackInfoCard's tier
  // selector can render one card per sibling pack. Clicking a card
  // fires `onSlideChange` (same handler the slider's swipe uses) so the
  // slider animates to that offer — and PackInfoCard, OddsPanel,
  // BottomAction all follow because they read from `activeOfferId`.
  //
  // Tier label = the offer's own `badgeName` (per-operator override aware,
  // flat field from ShortSerializer). The `suitableBadges` own-row lookup
  // stays as a fallback for older backends without the flat field; when
  // neither yields a name we fall back to the pack title so the card never
  // shows blank.
  const categoryOffersAsTiers: TierBadge[] = categoryOffers.map((o) => {
    const ownBadge =
      o.suitableBadges.find((b) => b.poolOfferId === o.id) ??
      o.suitableBadges.find((b) => b.isSelf);
    return {
      poolOfferId: o.id,
      name: o.badgeName || ownBadge?.name?.trim() || o.title,
      price: o.price,
      imageUrl: o.packImageUrl,
      subImageUrl: null,
      isSelf: activeOfferId !== null && o.id === activeOfferId,
    };
  });

  // Slider needs at least the current pack to render. When the category
  // index endpoint returns 0 offers (e.g. operator-scope filter excludes
  // siblings, or the URL `game_id` doesn't sit in any visible category),
  // synthesise a single-item list from `offer` so the centre pack still
  // shows. Slider already gates chevrons/dots/drag on `len > 1`, so the
  // synthesised single-item case renders as a static centred pack — no
  // controls, no peeks.
  const sliderOffers: PoolOfferListItem[] =
    categoryOffers.length > 0
      ? categoryOffers
      : offer
        ? [
            {
              id: offer.id,
              title: offer.title,
              price: offer.price,
              status: offer.status,
              isMultiple: false,
              packImageUrl: offer.packImageUrl,
              packImageAspect: offer.packImageAspect,
              packLogoImageUrl: offer.packLogoImageUrl,
              pedestalImageUrl: offer.pedestalImageUrl,
              suitableBadges: offer.suitableBadges,
              // Detail payload has no flat badge field — the synthesised
              // single slide never renders a tier row anyway.
              badgeName: null,
              operatorCurrency: offer.operatorCurrency,
              exchangeRate: offer.exchangeRate,
              demoMode: offer.demoMode,
            },
          ]
        : [];

  const oddsPanel = odds ? (
    <OddsPanel odds={odds} volatility={volatility} onVolatilityChange={onVolatilityChange} />
  ) : (
    <OddsPanelSkeleton />
  );

  return (
    // `@container` lives on a wrapper with NO padding so its content-box
    // size (which is what `@container size` queries against) matches the
    // viewport. If we put `px-X` on the same element, the padding
    // shrinks the content-box and rules like
    // `@[800px]:grid-cols-...` stop firing until viewport ≈ 848 (800 +
    // 48 of padding) — a Tailwind 4 / CSS Container-Query gotcha.
    <div className="@container">
      <div
        ref={layoutRef}
        className="
          relative pb-8 @[600px]:px-6
          mx-auto max-w-[1218px]
          grid grid-cols-1 gap-2
          @[800px]:grid-cols-[minmax(0,1fr)_360px]
          @[800px]:gap-x-6 @[800px]:gap-y-3
          @[1200px]:grid-cols-[minmax(0,1fr)_440px] @[1200px]:gap-x-[93px]
        "
      >
        {/* MOBILE-ONLY demo entry (Figma mobile ref) — top-left under the
            sticky Header, floating OVER the slider/pack art rather than
            taking a row, so toggling demo mode never shifts the page. The
            left offset matches the grid gutter (20px, +24px padding from
            600px). z-30 clears the slider's swipe layer (z-25) so the tap
            lands on the button. Desktop shows the same button lower,
            inside the sidebar (see below) — this one is hidden there. */}
        {showDemo && onOpenDemo && (
          <div className="@[800px]:hidden absolute top-0 left-5 @[600px]:left-11 z-30">
            <DemoRipButton onClick={onOpenDemo} />
          </div>
        )}

        {/* HERO / SLIDER — left column, row 1.
            On tablet (600–800, single column) the slider bleeds BOTH
            sides of the grid's px-6 so the peek packs get clipped by the
            viewport edge — same edge-cut look as mobile — instead of a
            hard cut 24px inside the page. On desktop it bleeds left only
            (px-6 at 800–900, px-8 above) so it runs from the viewport's
            left edge to the sidebar gap, matching Figma 543:2118 where
            the slider area starts flush against the frame's left edge.
            Width is grown by the same amount so the right edge stays
            where the grid put it. */}
        <div
          ref={sliderRef}
          className="
            relative
            @[600px]:-mx-6 @[600px]:w-[calc(100%+3rem)]
            @[800px]:col-start-1 @[800px]:row-start-1
            @[800px]:mr-0 @[800px]:-ml-6 @[800px]:w-[calc(100%+1.5rem)]
          "
        >
          <Slider
            offers={sliderOffers}
            activeOfferId={activeOfferId}
            phoneRoom={phoneRoom}
            {...(onSlideChange ? { onSlideChange } : {})}
          />
        </div>

        {/* MOBILE-ONLY under the pack title: the "What's Inside" pill with the
            slider dots below it, both centred (the site's phone storefront —
            no promo-code pill to share the row with). Tapping a dot fires the
            same `onSlideChange` as a swipe or a tier card. */}
        <div className="@[800px]:hidden flex flex-col items-center gap-2 px-4 pt-2 pb-1">
          <WhatsInsideButton onClick={() => setWhatsInsideOpen(true)} />
          {categoryOffers.length > 1 && (
            <div
              data-co-component="SliderDots"
              className="flex h-1.5 items-center gap-2"
            >
              {categoryOffers.map((o) => {
                const active = activeOfferId !== null && o.id === activeOfferId;
                return (
                  <button
                    key={o.id}
                    type="button"
                    aria-label={`Go to ${o.title}`}
                    aria-current={active || undefined}
                    onClick={() => onSlideChange?.(o.id)}
                    className={
                      'rounded-full transition-all cursor-pointer ' +
                      (active ? 'h-1.5 w-1.5' : 'h-1 w-1')
                    }
                    // Colour flows through `--co-SliderDots-{active,idle}` so
                    // the Design Constructor recolours every dot at once.
                    style={{
                      background: active
                        ? 'var(--co-SliderDots-active, rgba(255,255,255,1))'
                        : 'var(--co-SliderDots-idle, rgba(255,255,255,0.5))',
                    }}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* SIDEBAR — right column. On desktop a peek pack from the
            slider may slide *behind* the sidebar (no hard clip at the
            slider edge), so the sidebar is given a higher z-index and
            `relative` to overlay any peek silhouette that bleeds into
            its column. The cards themselves are GLASS_SURFACE-blurred,
            so the peek reads as a soft shape under the sidebar.
            z-30 sits below the sticky header (z-40). */}
        {/* Desktop: the sidebar shares the first row with the slider only;
            Just pulled and the potential hits run the full width under both
            (Figma 19074:253777). */}
        <aside
          className="
            flex flex-col gap-3
            @[800px]:col-start-2 @[800px]:row-start-1
            @[800px]:relative @[800px]:z-30
          "
        >
          <PackInfoCard
            title={offer?.title ?? ''}
            tiers={categoryOffersAsTiers}
            description={offer?.description ?? null}
            {...(offer ? { buybackPercent: offer.buybackPercent } : {})}
            {...(onSlideChange ? { onTierSelect: onSlideChange } : {})}
          >
            {oddsPanel}
          </PackInfoCard>

          {/* Phone: the same Max Pull block straight on the scene. */}
          <div className="@[800px]:hidden [--statsRow-phoneScale:0.84]">{oddsPanel}</div>
          <FairnessTicketKeeper offerId={offer?.id} />

          {/* Desktop demo entry — between odds and the RIP action, matching
              the sidebar ordering in the Figma reference. Mobile shows the
              same button up near the Header instead (see above). */}
          {showDemo && onOpenDemo && (
            <div className="hidden @[800px]:block">
              <DemoRipButton onClick={onOpenDemo} fullWidth />
            </div>
          )}

          <div className="hidden @[800px]:block">
            <BottomAction
              quantity={quantity}
              onQuantityChange={onQuantityChange ?? (() => {})}
              onBuy={onBuy ?? (() => {})}
              unitPrice={unitPrice}
              {...(maxForPurchaseCount !== undefined ? { maxQty: maxForPurchaseCount } : {})}
              {...(unavailableLabel ? { unavailableLabel } : {})}
            />
          </div>

          {/* Help — desktop only, directly under the RIP rail. The wrapper
              goes `contents` (not `block`) at ≥800px so the anchor itself
              becomes the aside's flex item: with the operator's knob off the
              anchor is `display:none` and drops out of the flex flow
              entirely, where a wrapper box would still have claimed one
              `gap-2` step under the rail. */}
          <div className="hidden @[800px]:contents">
            <HelpLink className="self-center" />
          </div>
        </aside>

        {/* DESKTOP-ONLY Just pulled — the active pack's latest pulls. */}
        <div className="hidden @[800px]:block @[800px]:col-span-2 @[800px]:row-start-2 @[800px]:pt-6">
          <JustPulledStrip
            items={justPulledItems}
            loading={justPulledLoading}
            {...(onShowJustPulled ? { onShowAll: onShowJustPulled } : {})}
          />
        </div>

        {/* PotentialHits — desktop only, left column row 3; the phone shows
            them in the "What's inside?" sheet. */}
        <div className="hidden @[800px]:block @[800px]:col-span-2 @[800px]:row-start-3 @[800px]:pt-6">
          <PotentialHitsByTier items={potentialHits} loading={potentialHitsLoading} />
        </div>

        {/* Help — mobile only: the end of the scroll, under Potential Hits.
            Desktop has it in the sidebar under the RIP rail instead, where
            the mobile rail's fixed position leaves no room for it. */}
        <div className="contents @[800px]:hidden">
          <HelpLink className="justify-self-center" />
        </div>

        {/* Mobile spacer — reserves 156px (the RIP disc's reach: 16px
            bottom offset + 140px disc) under the in-flow content so it
            never slides under the portal'd `MobileBar` (which lives in
            `<body>` and is fixed to the viewport bottom). The bar itself
            is rendered exactly once from the desktop `<BottomAction>`
            mount inside the sidebar — its `MobileBar` child portals to
            body globally, so a second mount here would create a
            duplicate rail in `<body>`. */}
        <div
          className="@[800px]:hidden"
          style={{ height: MOBILE_RAIL_SPACER_HEIGHT }}
          aria-hidden
        />
      </div>

      <AnimatePresence>
        {whatsInsideOpen && (
          <WhatsInsideSheet
            potentialHits={potentialHits}
            justPulledItems={justPulledItems}
            justPulledLoading={justPulledLoading}
            {...(onShowJustPulled ? { onShowJustPulled } : {})}
            onClose={() => setWhatsInsideOpen(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
